import { Disclosure } from "@headlessui/react";
import { Fragment, useState, useContext } from "react";
import { Dialog, Menu, Transition } from "@headlessui/react";
import {
  BellIcon,
  ChartBarIcon,
  AdjustmentsIcon,
  HomeIcon,
  LightningBoltIcon,
  TruckIcon,
  MenuAlt2Icon,
  XIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentReportIcon,
  ClipboardCheckIcon,
  ReceiptTaxIcon,
} from "@heroicons/react/outline";
import AppContext from "../context/app-context";
import "react-sliding-pane/dist/react-sliding-pane.css";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "@progress/kendo-react-tooltip";

const nav = [
  {
    name: "Home",
    href: "home",
    icon: HomeIcon,
    current: true,
    disabledRole: 300,
  },
  // {
  //   name: "Planner",
  //   href: "planner",
  //   icon: ClipboardCheckIcon,
  //   current: false,
  // },
  { name: "PO", href: "poes/all", icon: LightningBoltIcon, current: false },
  {
    name: "Quotations",
    href: "quotations",
    icon: DocumentReportIcon,
    disabledRole: 300,
    current: false,
  },
  {
    name: "Jobs",
    href: "jobs",
    disabledRole: 300,
    icon: TruckIcon,
    current: false,
  },
  // {
  //   name: "Invoices",
  //   href: "",
  //   icon: ReceiptTaxIcon,
  //   disabledRole: 300,
  //   current: false,
  //   children: [
  //     { name: "Supplier Invoices", href: "invoices/supplier", current: false },
  //     { name: "Customer Invoices", href: "invoices/customer", current: false },
  //   ],
  // },
  {
    name: "Masters",
    href: "",
    icon: AdjustmentsIcon,
    disabledRole: 300,
    current: false,
    children: [
      { name: "User", href: "user", current: false },
      { name: "Customer", href: "customer", current: false },
      { name: "Supplier", href: "supplier", current: false },
      { name: "Agent", href: "agent", current: false },
      { name: "Party", href: "party", current: false },
      { name: "Vessel", href: "vessel", current: false },
      { name: "Warehouse", href: "warehouse", current: false },
      { name: "Port", href: "port", current: false },
      { name: "City", href: "city", current: false },
      { name: "Charge", href: "charge", current: false },
      { name: "Tariff", href: "tariff", current: false },
      { name: "Currency", href: "currency", current: false },
      { name: "Exchange", href: "exchange", current: false },
    ],
  },
  {
    name: "Reports",
    href: "",
    icon: ChartBarIcon,
    current: false,
    children: [
      { name: "Stock", href: "stock", current: false },
      // { name: "Un-Invoiced", href: "uninvoiced", current: false },
    ],
  },
];
const userNavigation = [{ name: "Sign out", href: "#" }];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Layout({ children }) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navigation, setNavigation] = useState(nav);
  const [activeSubmenu, setActiveSubmenu] = useState(''); // Add state for tracking active submenu
  const authCtx = useContext(AppContext);
  const onLogout = () => {
    authCtx.logout();
    navigate("/#");
  };
  const [open, setOpen] = useState(false);

  // Handler for menu item clicks
  const handleMenuClick = (itemName) => {
    setActiveSubmenu(activeSubmenu === itemName ? '' : itemName);
    setNavigation((prev) =>
      prev.map((n) =>
        n.name === itemName
          ? { ...n, current: true }
          : { ...n, current: false }
      )
    );
  };

  // Handler for submenu item clicks
  const handleSubmenuClick = (itemName, subItemName) => {
    setActiveSubmenu('');
    setNavigation((prev) =>
      prev.map((n) =>
        n.name === itemName
          ? {
              ...n,
              children: n.children.map((c) =>
                c.name === subItemName
                  ? { ...c, current: true }
                  : { ...c, current: false }
              ),
            }
          : n
      )
    );
  };

  // Add new function to calculate submenu position
  const getSubmenuPosition = (index, totalItems) => {
    const itemHeight = 40; // approximate height of each menu item
    const menuHeight = totalItems * itemHeight;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 0;
    const maxTop = viewportHeight - menuHeight - 20; // 20px padding from bottom

    // Calculate ideal position
    const idealTop = index * itemHeight;

    // Constrain position to keep submenu visible
    return Math.min(Math.max(0, idealTop), maxTop);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 flex z-50 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600/75 backdrop-blur-sm" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex flex-col w-full max-w-xs bg-slate-800">
              <div className="absolute top-0 right-0 pt-4 -mr-12">
                <button
                  type="button"
                  className="flex items-center justify-center w-10 h-10 rounded-full focus:outline-none"
                  onClick={() => setSidebarOpen(false)}
                >
                  <XIcon className="w-6 h-6 text-white" aria-hidden="true" />
                </button>
              </div>

              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex items-center justify-center px-4 bg-slate-900 py-4 mb-4">
                  <img className="w-auto h-12" src="logo.png" alt="VML" />
                </div>

                <nav className="px-3 mt-4 space-y-2">
                  {navigation
                    .filter((x) => x.disabledRole !== authCtx.profile.role)
                    .map((item) =>
                      !item.children ? (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => {
                            setNavigation((prev) =>
                              prev.map((n) =>
                                n.name === item.name
                                  ? { ...n, current: true }
                                  : { ...n, current: false }
                              )
                            );
                            setSidebarOpen(false);
                          }}
                          className={classNames(
                            item.current
                              ? "bg-indigo-600 text-white"
                              : "text-gray-300 hover:bg-slate-700 hover:text-white",
                            "group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition duration-150"
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-white"
                                : "text-gray-400 group-hover:text-white",
                              "mr-3 flex-shrink-0 h-5 w-5 transition duration-150"
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </Link>
                      ) : (
                        <Disclosure as="div" key={item.name} className="space-y-1">
                          {({ open }) => (
                            <>
                              <Disclosure.Button
                                className={classNames(
                                  item.current
                                    ? "bg-indigo-600 text-white"
                                    : "text-gray-300 hover:bg-slate-700 hover:text-white",
                                  "group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition duration-150"
                                )}
                              >
                                <item.icon
                                  className={classNames(
                                    item.current
                                      ? "text-white"
                                      : "text-gray-400 group-hover:text-white",
                                    "mr-3 flex-shrink-0 h-5 w-5 transition duration-150"
                                  )}
                                  aria-hidden="true"
                                />
                                <span className="flex-1">{item.name}</span>
                                <ChevronRightIcon
                                  className={classNames(
                                    open ? "rotate-90 transform" : "",
                                    "flex-shrink-0 w-4 h-4 ml-1 text-gray-400 transition-transform duration-150"
                                  )}
                                />
                              </Disclosure.Button>
                              <Disclosure.Panel className="px-3 space-y-1">
                                {item.children.map((subItem) => (
                                  <Link
                                    key={subItem.name}
                                    to={subItem.href}
                                    onClick={() => setSidebarOpen(false)}
                                    className="group flex items-center pl-10 pr-2 py-2 text-sm font-medium text-gray-300 rounded-md hover:text-white hover:bg-slate-700 transition duration-150"
                                  >
                                    {subItem.name}
                                  </Link>
                                ))}
                              </Disclosure.Panel>
                            </>
                          )}
                        </Disclosure>
                      )
                    )}
                </nav>
              </div>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition.Root>

      {/* Desktop Sidebar */}
      <div
        className={classNames(
          open ? "w-64" : "w-20",
          "hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col z-30 transition-all duration-300 ease-in-out"
        )}
      >
        <div className="flex flex-col flex-1 min-h-0 bg-slate-800">
          {/* Logo Section */}
          <div className="flex-shrink-0 bg-slate-900 border-b border-slate-700">
            <div
              className={classNames(
                "flex items-center justify-center bg-white",
                open ? "h-16 px-4" : "h-16 px-2"
              )}
            >
              <img
                className={classNames(
                  "transition-all duration-300",
                  open ? "h-8 w-auto" : "h-10 w-auto"
                )}
                src="logo.png"
                alt="VML"
              />
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-y-auto">
            <nav className="flex-1 px-3 py-4 space-y-2">
              {navigation
                .filter((x) => x.disabledRole !== authCtx.profile.role)
                .map((item, index) =>
                  !item.children ? (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => {
                        setActiveSubmenu('');
                        setNavigation((prev) =>
                          prev.map((n) =>
                            n.name === item.name
                              ? { ...n, current: true }
                              : { ...n, current: false }
                          )
                        );
                      }}
                      className={classNames(
                        item.current
                          ? "bg-indigo-600 text-white"
                          : "text-gray-300 hover:bg-slate-700 hover:text-white",
                        "group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition duration-150",
                        !open && "justify-center"
                      )}
                    >
                      <item.icon
                        className={classNames(
                          item.current
                            ? "text-white"
                            : "text-gray-400 group-hover:text-white",
                          "flex-shrink-0 h-5 w-5 transition duration-150"
                        )}
                        aria-hidden="true"
                      />
                      {open && <span className="ml-3">{item.name}</span>}
                    </Link>
                  ) : (
                    <div key={item.name} className="space-y-1 relative">
                      <button
                        onClick={() => handleMenuClick(item.name)}
                        className={classNames(
                          item.current
                            ? "bg-indigo-600 text-white"
                            : "text-gray-300 hover:bg-slate-700 hover:text-white",
                          "group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition duration-150",
                          !open && "justify-center"
                        )}
                      >
                        <item.icon
                          className={classNames(
                            item.current
                              ? "text-white"
                              : "text-gray-400 group-hover:text-white",
                            "flex-shrink-0 h-5 w-5 transition duration-150"
                          )}
                          aria-hidden="true"
                        />
                        {open && (
                          <>
                            <span className="ml-3">{item.name}</span>
                            <ChevronRightIcon
                              className={classNames(
                                activeSubmenu === item.name ? "rotate-90 transform" : "",
                                "flex-shrink-0 w-4 h-4 ml-auto text-gray-400 transition-transform duration-150"
                              )}
                            />
                          </>
                        )}
                      </button>

                      {/* Submenu */}
                      {((!open && activeSubmenu === item.name) || (open && activeSubmenu === item.name)) && (
                        <div
                          className={classNames(
                            !open ? "fixed left-20 z-50 ml-1" : "mt-1 pl-7",
                            "min-w-[14rem]"
                          )}
                          style={!open ? {
                            top: index * 40 + "px"
                          } : undefined}
                        >
                          <div className={classNames(
                            !open ? "bg-slate-700 rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 py-1" : "space-y-1"
                          )}>
                            {item.children.map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.href}
                                onClick={() => handleSubmenuClick(item.name, subItem.name)}
                                className={classNames(
                                  subItem.current
                                    ? "bg-indigo-600 text-white"
                                    : "text-gray-300 hover:bg-slate-600 hover:text-white",
                                  "block px-4 py-2 text-sm transition duration-150 rounded-md"
                                )}
                              >
                                {subItem.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div
        className={classNames(
          "flex flex-col flex-1",
          open ? "lg:pl-64" : "lg:pl-20"
        )}
      >
        {/* Top Navbar */}
        <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white  border-b border-gray-200">
          <button
            type="button"
            className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <MenuAlt2Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          <div className="flex flex-1 justify-between px-4">
            <div className="flex items-center">
              <button
                onClick={() => setOpen(!open)}
                className="text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 p-1.5 rounded-lg transition duration-150"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h7"
                  />
                </svg>
              </button>
            </div>

            <div className="ml-4 flex items-center gap-4">
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center max-w-xs rounded-lg bg-white p-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition duration-150">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-700">
                    <span className="text-lg font-medium text-white">
                      {authCtx.profile.displayname.toString().substring(0, 1)}
                    </span>
                  </span>
                  <div className="ml-3 text-left hidden md:block">
                    <p className="text-sm font-semibold text-gray-900">
                      {authCtx.profile.displayname}
                    </p>
                    <p className="text-xs text-gray-500">
                      {authCtx.profile.jobTitle}
                    </p>
                  </div>
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {userNavigation.map((item) => (
                      <Menu.Item key={item.name}>
                        {({ active }) => (
                          <button
                            onClick={onLogout}
                            className={classNames(
                              active ? "bg-gray-50" : "",
                              "block w-full px-4 py-2 text-left text-sm text-gray-700 hover:text-indigo-600 transition duration-150"
                            )}
                          >
                            {item.name}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>

              <button
                onClick={onLogout}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition duration-150"
              >
                <img src="logout.svg" className="h-5 w-5" alt="Logout" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="py-6">
            <div className="mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
}
